import React from 'react';

import { ObjectFieldTemplateProps, RJSFSchema } from '@rjsf/utils';

import { Box } from '@mui/material';

/**
 * GridObjectFieldTemplate - A custom RJSF ObjectFieldTemplate that renders fields
 * in a responsive 12-column CSS Grid layout.
 *
 * Features:
 * - 12-column grid on xl screens (1536px+), single column on mobile
 * - Configurable fields-per-row via ui:options.gridColumns (default: 4)
 * - Per-field widths via ui:colSpan in uiSchema (out of 12)
 * - Opt-in via ui:options.gridLayout in uiSchema
 *
 * Usage:
 * ```tsx
 * const uiSchema = {
 *   'ui:options': { gridLayout: true, gridColumns: 4 }, // 4 fields per row
 *   'ui:order': ['field1', 'field2', ...],
 *   fieldName: { 'ui:colSpan': 12 },  // Full width
 *   otherField: { 'ui:colSpan': 6 },  // Half width
 *   // Fields without ui:colSpan default to 12/gridColumns (e.g., 3 for 4 columns)
 * };
 * ```
 *
 * Column span values (out of 12):
 * - 3 = 25% width (default for gridColumns: 4)
 * - 4 = 33% width (default for gridColumns: 3)
 * - 6 = 50% width
 * - 12 = 100% width (full row)
 */

const GRID_COLUMNS = 12;
const DEFAULT_FIELDS_PER_ROW = 4;

interface GridObjectFieldTemplateProps extends ObjectFieldTemplateProps<unknown, RJSFSchema> {
  uiSchema?: {
    'ui:options'?: {
      gridLayout?: boolean;
      gridColumns?: number;
    };
    [key: string]: unknown;
  };
}

export default function GridObjectFieldTemplate({
  properties,
  uiSchema,
  title,
  description,
  fieldPathId,
}: GridObjectFieldTemplateProps) {
  const gridLayoutEnabled = uiSchema?.['ui:options']?.gridLayout;
  const fieldsPerRow = uiSchema?.['ui:options']?.gridColumns ?? DEFAULT_FIELDS_PER_ROW;
  const defaultColSpan = Math.floor(GRID_COLUMNS / fieldsPerRow);

  // If grid layout is not enabled, render with default stacking behavior
  if (!gridLayoutEnabled) {
    return (
      <Box component="fieldset" id={fieldPathId.$id} sx={{ border: 'none', p: 0, m: 0 }}>
        {title && <legend>{title}</legend>}
        {description}
        {properties.map((element) => (
          <Box key={element.name} sx={{ mb: 2 }}>
            {element.content}
          </Box>
        ))}
      </Box>
    );
  }

  // Grid layout enabled - render in responsive CSS Grid
  return (
    <Box component="fieldset" id={fieldPathId.$id} sx={{ border: 'none', p: 0, m: 0 }}>
      {title && <legend>{title}</legend>}
      {description}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: `repeat(${GRID_COLUMNS}, 1fr)`,
          },
          gap: 2,
        }}
      >
        {properties.map((element) => {
          const fieldUiSchema = uiSchema?.[element.name] as Record<string, unknown> | undefined;
          const colSpan = (fieldUiSchema?.['ui:colSpan'] as number) ?? defaultColSpan;

          // Hide hidden fields
          if (element.hidden) {
            return (
              <Box key={element.name} sx={{ display: 'none' }}>
                {element.content}
              </Box>
            );
          }

          return (
            <Box
              key={element.name}
              sx={{
                gridColumn: {
                  xs: 'span 1',
                  lg: `span ${colSpan}`,
                },
              }}
            >
              {element.content}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
